import * as Yup from 'yup';

/**
 * Validation schema for individual interview questions
 */
const questionSchema = Yup.object({
  id: Yup.string().required('msg.idRequired'),
  question: Yup.string().trim().required('msg.thisFieldIsRequired'),
  media: Yup.mixed(),
  follow_up_question: Yup.string().nullable(),
  probing_goal: Yup.string().nullable(),
  answer_quantification: Yup.array().of(
    Yup.object({
      quantify_answer: Yup.boolean(),
      value: Yup.string().nullable(),
    }),
  ),
});

/**
 * Validation schema for interview question groups (with recursive subgroup support)
 * @param isRoot - Whether this is the root group (root groups don't require an id)
 * @param depth - Nesting depth (0 = root, 1 = first-level group, 2+ = subgroup)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createGroupSchema = (isRoot = false, depth = 0): Yup.ObjectSchema<any> =>
  Yup.object({
    ...(isRoot
      ? {} // root doesn't need id
      : { id: Yup.string().required('msg.idRequired') }),

    name: Yup.string().trim().required('msg.groupNameRequired'),

    description: Yup.string().nullable(),
    randomize: Yup.boolean(),

    children: Yup.array()
      .of(
        Yup.lazy((child) => {
          if (!child || typeof child !== 'object') return Yup.mixed();
          if ('question' in child) return questionSchema;
          if ('children' in child) return createGroupSchema(false, depth + 1); // subgroup
          return Yup.mixed();
        }),
      )
      // Root group can be empty, but nested groups/subgroups must have at least one child
      .test('not-empty', (val, context) => {
        if (isRoot) return true;
        if (!Array.isArray(val) || val.length === 0) {
          // depth 1 = first-level group, depth 2+ = subgroup
          const errorKey = depth === 1 ? 'msg.groupCannotBeEmpty' : 'msg.subgroupCannotBeEmpty';
          return context.createError({ message: errorKey });
        }
        return true;
      }),
  });

/**
 * Root-level schema for interview questions structure
 */
const interviewQuestionsSchema = Yup.lazy((value) => {
  if (!value) return Yup.mixed().nullable();
  return createGroupSchema(true, 0); // root group (no id required, depth 0)
});

/**
 * Main validation schema for project creation/update
 * @param min - Minimum number of respondents (used for published projects)
 * @param isProjectPublished - Whether the project is already published
 */
export const createProjectValidationSchema = (min: number, isProjectPublished: boolean) => {
  return Yup.object().shape({
    type_of_interview: Yup.array().of(Yup.string()),
    interviewer_name: Yup.string(),
    company_name: Yup.string(),
    interview_goal: Yup.string(),
    interview_audience: Yup.string(),
    tone_of_voice: Yup.string(),
    privacy_policy: Yup.string().url('msg.pleaseEnterValidUrl'),
    language: Yup.string(),
    analysis_language: Yup.string(),
    allow_language_selection: Yup.boolean().nullable(),
    theme_id: Yup.string(),
    snippets: Yup.array(),
    opening_message: Yup.string(),
    respondents_max: Yup.number()
      .required('msg.thisFieldIsRequired')
      .min(
        isProjectPublished ? min : 0,
        isProjectPublished
          ? `msg.numberMustBeGreaterThanOrEqualTo`
          : 'msg.numberMustBeGreaterThanZero',
      )
      .max(1000000, 'msg.numberMustBeLessThanOrEqualTo1000000')
      .nullable()
      .typeError('msg.thisFieldIsRequired'),
    interview_questions: interviewQuestionsSchema,
    screening_questions: Yup.array().of(
      Yup.object()
        .shape({
          id: Yup.string(),
          question: Yup.string().trim().required('msg.thisFieldIsRequired'),
          type: Yup.string(),
          quotas_enabled: Yup.boolean(),
          responses: Yup.lazy((value) =>
            Array.isArray(value)
              ? Yup.array()
                  .of(
                    Yup.object().shape({
                      id: Yup.string(),
                      value: Yup.string().trim().required('msg.thisFieldIsRequired'),
                      rule: Yup.string().nullable().required('msg.pleaseSelectOneOfTheRules'),
                      quota_percentage: Yup.number().nullable(),
                    }),
                  )
                  .nullable()
              : Yup.mixed().nullable(),
          ),
          numericResponse: Yup.lazy((value) =>
            typeof value === 'object' && value !== null
              ? Yup.object().shape({
                  minimum: Yup.number()
                    .nullable()
                    .transform((val) => (Number.isNaN(val) ? null : val)),
                  maximum: Yup.number()
                    .nullable()
                    .transform((val) => (Number.isNaN(val) ? null : val)),
                })
              : Yup.mixed().nullable(),
          ),
        })
        .test('quota-sum-100', 'msg.quotaPercentagesMustEqual100', function (question) {
          if (!question?.quotas_enabled) return true;
          const responses = question?.responses;
          if (!Array.isArray(responses)) return true;
          const qualifyingResponses = responses.filter((r) => r?.rule === 'qualify');
          if (qualifyingResponses.length === 0) return true;
          const sum = qualifyingResponses.reduce(
            (acc, r) => acc + (Number(r?.quota_percentage) || 0),
            0,
          );
          // Round to 3 decimal places before comparing to avoid floating-point precision
          // issues (e.g. 33.33 + 33.33 + 33.34 raw sum may drift just above the 0.01 tolerance)
          return Math.abs(Math.round(sum * 1000) / 1000 - 100) <= 0.01;
        }),
    ),
  });
};
